import { z } from 'zod';

/**
 * Public share link DTOs.
 *
 * A share link is a token-in-URL that lets anyone read a single
 * project or task without signing into Projoint.  Created by a
 * LEAD+ on the team; revocable at any time.
 */

export const PublicShareScopeSchema = z.enum(['project', 'task']);
export type PublicShareScope = z.infer<typeof PublicShareScopeSchema>;

export const PublicShareSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  scope: PublicShareScopeSchema,
  projectId: z.string().uuid().nullable(),
  taskId: z.string().uuid().nullable(),
  token: z.string(),
  label: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  viewCount: z.number().int().nonnegative(),
  lastViewedAt: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  /** Human-readable name of the target — `<project name>` or
   *  `<project key>-<task number>: <title>`.  Populated by the
   *  service for the management list. */
  targetLabel: z.string(),
});
export type PublicShareProjection = z.infer<typeof PublicShareSchema>;
export const PublicShareListSchema = z.array(PublicShareSchema);

export const CreateProjectShareSchema = z
  .object({
    projectId: z.string().uuid(),
    label: z.string().min(1).max(80).optional(),
    expiresInDays: z.number().int().positive().max(365).nullable().optional(),
  })
  .strict();
export type CreateProjectShareInput = z.infer<typeof CreateProjectShareSchema>;

export const CreateTaskShareSchema = z
  .object({
    taskId: z.string().uuid(),
    label: z.string().min(1).max(80).optional(),
    expiresInDays: z.number().int().positive().max(365).nullable().optional(),
  })
  .strict();
export type CreateTaskShareInput = z.infer<typeof CreateTaskShareSchema>;

// ── Public-side read shapes (anonymous viewer) ─────────────────────────────

/** Returned by GET /api/public/share/:token when the share targets a project. */
export const PublicProjectViewSchema = z.object({
  kind: z.literal('project'),
  share: z.object({
    label: z.string().nullable(),
    viewCount: z.number().int().nonnegative(),
    expiresAt: z.string().nullable(),
  }),
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    key: z.string(),
    color: z.string().nullable(),
    icon: z.string().nullable(),
    description: z.string().nullable(),
  }),
  team: z.object({ name: z.string() }),
  tasks: z.array(
    z.object({
      id: z.string().uuid(),
      key: z.string(),
      title: z.string(),
      statusName: z.string(),
      statusColor: z.string(),
      statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
      priority: z.number().int().min(1).max(4),
      dueDate: z.string().nullable(),
      assigneeName: z.string().nullable(),
    }),
  ),
  milestones: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      color: z.string(),
      plannedStartDate: z.string().nullable(),
      plannedEndDate: z.string().nullable(),
      progressPct: z.number().int().min(0).max(100),
      status: z.enum(['planned', 'active', 'at_risk', 'completed', 'overdue']),
    }),
  ),
});
export type PublicProjectView = z.infer<typeof PublicProjectViewSchema>;

/** Returned by GET /api/public/share/:token when the share targets a task. */
export const PublicTaskViewSchema = z.object({
  kind: z.literal('task'),
  share: z.object({
    label: z.string().nullable(),
    viewCount: z.number().int().nonnegative(),
    expiresAt: z.string().nullable(),
  }),
  task: z.object({
    id: z.string().uuid(),
    key: z.string(),
    title: z.string(),
    descriptionText: z.string().nullable(),
    statusName: z.string(),
    statusColor: z.string(),
    statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
    priority: z.number().int().min(1).max(4),
    dueDate: z.string().nullable(),
    assigneeName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  project: z.object({ name: z.string(), key: z.string() }),
  team: z.object({ name: z.string() }),
});
export type PublicTaskView = z.infer<typeof PublicTaskViewSchema>;

export const PublicShareViewSchema = z.discriminatedUnion('kind', [
  PublicProjectViewSchema,
  PublicTaskViewSchema,
]);
export type PublicShareView = z.infer<typeof PublicShareViewSchema>;
